import React from 'react';
import {
  Reservation,
  ReservationSummary,
  PaginationResult,
} from '../../dashboard/types';
import { Crown } from 'lucide-react';
import { getStatusBadge } from '../../ui/ReservationStatusBadge';

interface ReservationsTabProps {
  summary: ReservationSummary | null;
  trendMax: number;
  nextHourReservations: Reservation[];
  reservations: PaginationResult<Reservation> | null;
  page: number;
  onSelectReservation: (reservationId: string) => void;
  onPageChange: (page: number) => void;
}

export const ReservationsTab: React.FC<ReservationsTabProps> = ({
  summary,
  trendMax,
  nextHourReservations,
  reservations,
  page,
  onSelectReservation,
  onPageChange,
}) => {
  return (
    <>
      <section className='metric-row'>
        <article className='metric-card'>
          <p>Total this week</p>
          <strong>{summary?.thisWeekCount ?? 0}</strong>
        </article>

        <article className='metric-card'>
          <p>Available Tables</p>
          <div className='w-full mt flex justify-between items-center gap-2'>
            <strong className='text-sm'>
              VIP - {summary?.availableVipTables ?? 0}
            </strong>
            <strong className='text-sm'>
              Regular - {summary?.availableRegularTables ?? 0}
            </strong>
          </div>
        </article>
        <article className='metric-card'>
          <p>Paid Reservations</p>
          <strong>{summary?.paidReservations ?? 0}</strong>
        </article>
        <article className='metric-card'>
          <p>Today's Reservations</p>
          <strong>{summary?.todayCount ?? 0}</strong>
        </article>
      </section>

      <section className='panel-row'>
        <article className='panel-card'>
          <header>
            <h2>Most Popular Days</h2>
          </header>
          <div className='trend-chart'>
            {(summary?.trendLast7Days || []).map((point) => (
              <div
                className='trend-day'
                key={point.date}>
                <div className='trend-bars'>
                  <div
                    className='bar total'
                    style={{
                      height: `${(point.totalCount / trendMax) * 100}%`,
                    }}
                    title={`Total: ${point.totalCount}`}
                  />
                  <div
                    className='bar cancelled'
                    style={{
                      height: `${(point.cancelledCount / trendMax) * 100}%`,
                    }}
                    title={`Cancelled: ${point.cancelledCount}`}
                  />
                </div>
                <p>
                  {new Date(point.date).toLocaleDateString('en-US', {
                    weekday: 'short',
                  })}
                </p>
              </div>
            ))}
          </div>
        </article>
        <article className='panel-card mini'>
          <header>
            <h2>Pay Attention</h2>
          </header>
          <p>Due in the next hour: {nextHourReservations.length}</p>
          {nextHourReservations.length > 0 ? (
            <ul className='service-feed'>
              {nextHourReservations.slice(0, 5).map((reservation) => (
                <li
                  key={reservation.id}
                  className='service-item'>
                  <span className='service-name'>
                    {reservation.customerName}
                  </span>
                  <span className='service-meta'>
                    {reservation.timeStart} - {reservation.timeEnd} |{' '}
                    {reservation.numberOfGuests} guests
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p>No reservations due in the next hour.</p>
          )}
        </article>
      </section>

      <section className='table-card'>
        <header>
          <h2>Reservations</h2>
        </header>
        <div className='table-wrap'>
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                {/* <th>Email</th> */}
                <th>Date</th>
                <th>From</th>
                <th>To</th>
                <th>Guests</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {reservations?.items.map((reservation) => (
                <tr key={reservation.id}>
                  <td>{reservation.customerName}</td>
                  {/* <td>{reservation.customerEmail}</td> */}
                  <td>{reservation.date}</td>
                  <td>{reservation.timeStart}</td>
                  <td>{reservation.timeEnd}</td>
                  <td>{reservation.numberOfGuests}</td>
                  <td>
                    {getStatusBadge(reservation.status)}
                  </td>
                  <td className='flex justify-between items-center'>
                    <button onClick={() => onSelectReservation(reservation.id)}>
                      View
                    </button>
                    {reservation.isVip && (
                      <span className='px-2 text-sm rounded-lg text-yellow-500'>
                        <Crown size={24} />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className='pager'>
          <button
            disabled={!reservations?.hasPreviousPage}
            onClick={() => onPageChange(Math.max(1, page - 1))}>
            Previous
          </button>
          <span>
            Page {reservations?.currentPage || 1} of{' '}
            {reservations?.totalPages || 1}
          </span>
          <button
            disabled={!reservations?.hasNextPage}
            onClick={() => onPageChange(page + 1)}>
            Next
          </button>
        </div>
      </section>
    </>
  );
};
